import { Controller } from '@hotwired/stimulus';

// Fills the 2FA code shown by DevTools::TwoFactorCodeComponent into the nearest code form: one
// field (sign-in prompt, current-code forms) or the six one-digit boxes of the enrollment modals.
// The helper may sit outside the form (under the prompt card), so it climbs to the closest
// ancestor that holds a code field rather than assuming a shared parent. A helper rendered with
// field=current_code fills the current-code field a form asks for beside the code it verifies.
//
// It also counts down to the end of the current 30-second window and then reloads the Turbo frame
// it sits in, so the code on screen is always the one the prompt accepts. The frame's new content
// carries a fresh countdown, which connects a new instance of this controller.
export default class extends Controller {
  static codeSelector = 'input[name="two_step[code][]"], input[name="two_step[code]"]'
  static currentCodeSelector = 'input[name="two_step[current_code]"]'
  static targets = ['countdown']
  static values = { url: String, refreshIn: Number }

  connect() {
    if (!this.hasUrlValue) return;

    this.remaining = this.refreshInValue;
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnect() {
    clearInterval(this.timer);
  }

  fill({ params: { code, field } }) {
    const inputs = this.codeInputs(field);
    if (inputs.length === 0) return;

    const boxes = inputs.filter((input) => input.name === 'two_step[code][]');
    if (boxes.length > 1) {
      const digits = String(code);
      boxes.forEach((input, index) => (input.value = digits[index] || ''));
      boxes[0].form?.requestSubmit();
      return;
    }

    inputs[0].value = code;
    inputs[0].dispatchEvent(new Event('input', { bubbles: true }));
    inputs[0].focus();
  }

  tick() {
    this.remaining -= 1;
    if (this.remaining > 0) {
      this.countdownTargets.forEach((target) => (target.textContent = this.remaining));
      return;
    }

    clearInterval(this.timer);
    this.refresh();
  }

  refresh() {
    const frame = this.element.closest('turbo-frame');
    if (!frame) return;

    if (frame.src) {
      frame.reload();
    } else {
      frame.src = this.urlValue;
    }
  }

  // The current-code-only forms (turning a method off, replacing backup codes) have no code field,
  // so a plain helper falls back to the current-code field there.
  codeInputs(field) {
    const { codeSelector, currentCodeSelector } = this.constructor;
    if (field === 'current_code') return this.nearest(currentCodeSelector);

    const inputs = this.nearest(codeSelector);
    return inputs.length > 0 ? inputs : this.nearest(currentCodeSelector);
  }

  nearest(selector) {
    let container = this.element.closest('form') || this.element.parentElement;
    while (container && !container.querySelector(selector)) {
      container = container.parentElement;
    }
    return container ? Array.from(container.querySelectorAll(selector)) : [];
  }
}
